import KybMandateRepository from "@/core/kyb/repositories/KybMandateRepository";
import {
  ActivateKybMandateInput,
  CreateKybDelegationInput,
  CreateKybMandateInput,
  KybDependentAccess,
  KybMandate,
  KybMandateActivationEligibility,
  KybMandateActivationOutcome,
  KybMandateDocument,
  KybMandateDocumentDownload,
  KybMandateMfaChallenge,
  KybMandateProofType,
  KybMandateRevocationImpact,
  KybMandateRevocationReason,
  KybMandateRevocationReasonCode,
  KybMandateRevocationResult,
  KybMandateStatus,
  KybMandateType,
  KybMandateValidity,
  KybSignatureMode,
  RevokeKybMandateInput,
  UploadKybMandateDocumentInput,
} from "@/core/kyb/entities/KybMandate";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import {
  executeKybRequest,
  toKybException,
} from "../../http/services/kybRequest/kybRequest";
import kybMandateEndpoints from "../../api/kybMandateEndpoints/kybMandateEndpoints";

type KybMandateDto = {
  mandateId: string;
  caseId: string;
  mandataireId: string;
  type: KybMandateType;
  status: KybMandateStatus;
  perimeter: string;
  accountNumber?: string | null;
  operationTypes?: string[] | null;
  amountCeiling: number;
  currency: string;
  startDate: string;
  endDate: string | null;
  signatureMode: KybSignatureMode;
  mandataireNonClient: boolean;
  parentMandateId: string | null;
  createdBy: string;
  createdAt: string;
  validatedBy: string | null;
  validatedAt: string | null;
  revokedBy: string | null;
  revokedAt: string | null;
  revocationReason: KybMandateRevocationReasonCode | null;
  revocationComment: string | null;
  revocationParentMandateId: string | null;
};

type KybMandateDocumentDto = {
  mandateDocumentId: string;
  mandateId: string;
  proofType: KybMandateProofType;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  legalValidityDate: string | null;
  uploadedBy: string;
  uploadedAt: string;
};

type KybDependentAccessDto = {
  accessId: string;
  label: string;
  coveredByAnotherActiveMandate: boolean;
  willBeDeactivated: boolean;
};

type KybRevocationImpactDto = {
  mandateId: string;
  status: KybMandateStatus;
  accesses: KybDependentAccessDto[];
  delegations: { delegation: KybMandateDto; accesses: KybDependentAccessDto[] }[];
  accessesToDeactivate: number;
  accessesMaintained: number;
};

type KybRevocationDto = {
  mandate: KybMandateDto;
  alreadyRevoked: boolean;
  deactivatedAccesses: number;
  maintainedAccesses: number;
  cascadedDelegations: number;
};

type KybMfaChallengeDto = { sentTo: string; ttlMinutes: number; testCode?: string };

const orUndefined = <T>(value: T | null | undefined): T | undefined =>
  value ?? undefined;

const mapMandate = (dto: KybMandateDto): KybMandate => ({
  mandateId: dto.mandateId,
  caseId: dto.caseId,
  mandataireId: dto.mandataireId,
  type: dto.type,
  status: dto.status,
  perimeter: dto.perimeter,
  accountNumber: orUndefined(dto.accountNumber),
  operationTypes: dto.operationTypes ?? [],
  amountCeiling: dto.amountCeiling,
  currency: dto.currency,
  startDate: dto.startDate,
  endDate: orUndefined(dto.endDate),
  signatureMode: dto.signatureMode,
  mandataireNonClient: dto.mandataireNonClient,
  parentMandateId: orUndefined(dto.parentMandateId),
  createdBy: dto.createdBy,
  createdAt: dto.createdAt,
  validatedBy: orUndefined(dto.validatedBy),
  validatedAt: orUndefined(dto.validatedAt),
  revokedBy: orUndefined(dto.revokedBy),
  revokedAt: orUndefined(dto.revokedAt),
  revocationReason: orUndefined(dto.revocationReason),
  revocationComment: orUndefined(dto.revocationComment),
  revocationParentMandateId: orUndefined(dto.revocationParentMandateId),
});

const mapDocument = (dto: KybMandateDocumentDto): KybMandateDocument => ({
  mandateDocumentId: dto.mandateDocumentId,
  mandateId: dto.mandateId,
  proofType: dto.proofType,
  fileName: dto.fileName,
  contentType: dto.contentType,
  sizeBytes: dto.sizeBytes,
  legalValidityDate: orUndefined(dto.legalValidityDate),
  uploadedBy: dto.uploadedBy,
  uploadedAt: dto.uploadedAt,
});

const mapAccess = (dto: KybDependentAccessDto): KybDependentAccess => ({
  accessId: dto.accessId,
  label: dto.label,
  coveredByAnotherActiveMandate: dto.coveredByAnotherActiveMandate,
  willBeDeactivated: dto.willBeDeactivated,
});

const mapImpact = (dto: KybRevocationImpactDto): KybMandateRevocationImpact => ({
  mandateId: dto.mandateId,
  status: dto.status,
  accesses: dto.accesses.map(mapAccess),
  delegations: dto.delegations.map((d) => ({
    delegation: mapMandate(d.delegation),
    accesses: d.accesses.map(mapAccess),
  })),
  accessesToDeactivate: dto.accessesToDeactivate,
  accessesMaintained: dto.accessesMaintained,
});

const mapChallenge = (dto: KybMfaChallengeDto): KybMandateMfaChallenge => ({
  sentTo: dto.sentTo,
  ttlMinutes: dto.ttlMinutes,
  testCode: dto.testCode,
});

const stringList = (value: unknown): string[] | null =>
  Array.isArray(value) && value.every((item): item is string => typeof item === "string")
    ? value
    : null;

/** Gardes d'activation d'un 409 : le back les rend dans `data` ; `null` pour tout autre refus. */
function extractActivationBlockers(error: unknown): string[] | null {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const { type, data } = error as { type?: unknown; data?: unknown };
  if (type !== "CONFLICT") {
    return null;
  }
  const codes = stringList(data);
  return codes && codes.length > 0 ? codes : null;
}

export default class KybMandateRepositoryBackend implements KybMandateRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async listMandates(caseId: string): Promise<RepositoryResult<KybMandate[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybMandateDto[]>>({
          url: kybMandateEndpoints.mandates(caseId),
          withAuth: true,
        }),
      (dtos) => dtos.map(mapMandate),
    );
  }

  async getMandate(
    caseId: string,
    mandateId: string,
  ): Promise<RepositoryResult<KybMandate>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybMandateDto>>({
          url: kybMandateEndpoints.mandate(caseId, mandateId),
          withAuth: true,
        }),
      mapMandate,
    );
  }

  async createMandate(
    caseId: string,
    input: CreateKybMandateInput,
  ): Promise<RepositoryResult<KybMandate>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybMandateDto>, CreateKybMandateInput>({
          url: kybMandateEndpoints.mandates(caseId),
          body: input,
          withAuth: true,
        }),
      mapMandate,
    );
  }

  async listDocuments(
    caseId: string,
    mandateId: string,
  ): Promise<RepositoryResult<KybMandateDocument[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybMandateDocumentDto[]>>({
          url: kybMandateEndpoints.documents(caseId, mandateId),
          withAuth: true,
        }),
      (dtos) => dtos.map(mapDocument),
    );
  }

  async uploadDocument(
    caseId: string,
    mandateId: string,
    input: UploadKybMandateDocumentInput,
  ): Promise<RepositoryResult<KybMandateDocument>> {
    return executeKybRequest(
      () => {
        const formData = new FormData();
        formData.append("proofType", input.proofType);
        if (input.legalValidityDate) {
          formData.append("legalValidityDate", input.legalValidityDate);
        }
        formData.append("file", input.file);
        return this.httpClient.post<ApiResponse<KybMandateDocumentDto>, FormData>({
          url: kybMandateEndpoints.documents(caseId, mandateId),
          body: formData,
          withAuth: true,
        });
      },
      mapDocument,
    );
  }

  async getDocumentDownload(
    caseId: string,
    mandateId: string,
    mandateDocumentId: string,
  ): Promise<RepositoryResult<KybMandateDocumentDownload>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<
          ApiResponse<{ url: string; expiresInSeconds: number }>
        >({
          url: kybMandateEndpoints.documentDownload(
            caseId,
            mandateId,
            mandateDocumentId,
          ),
          withAuth: true,
        }),
      (dto) => ({ url: dto.url, expiresInSeconds: dto.expiresInSeconds }),
    );
  }

  async getValidity(
    caseId: string,
    mandateId: string,
  ): Promise<RepositoryResult<KybMandateValidity>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybMandateValidity>>({
          url: kybMandateEndpoints.validity(caseId, mandateId),
          withAuth: true,
        }),
      (dto) => ({ valid: dto.valid, code: dto.code, message: dto.message }),
    );
  }

  async getActivationEligibility(
    caseId: string,
    mandateId: string,
  ): Promise<RepositoryResult<KybMandateActivationEligibility>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybMandateActivationEligibility>>({
          url: kybMandateEndpoints.activationEligibility(caseId, mandateId),
          withAuth: true,
        }),
      (dto) => ({
        eligible: dto.eligible,
        blockers: dto.blockers.map((b) => ({ code: b.code, label: b.label })),
        warnings: (dto.warnings ?? []).map((w) => ({ code: w.code, label: w.label, count: w.count })),
      }),
    );
  }

  async requestActivationMfaChallenge(
    caseId: string,
    mandateId: string,
  ): Promise<RepositoryResult<KybMandateMfaChallenge>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybMfaChallengeDto>, undefined>({
          url: kybMandateEndpoints.activationMfaChallenge(caseId, mandateId),
          withAuth: true,
        }),
      mapChallenge,
    );
  }

  async activateMandate(
    caseId: string,
    mandateId: string,
    input: ActivateKybMandateInput,
  ): Promise<RepositoryResult<KybMandateActivationOutcome>> {
    try {
      const response = await this.httpClient.post<
        ApiResponse<KybMandateDto>,
        ActivateKybMandateInput
      >({
        url: kybMandateEndpoints.activation(caseId, mandateId),
        body: input,
        withAuth: true,
      });
      return RepositoryResultFactory.success({
        outcome: "activated",
        mandate: mapMandate(response.data),
      });
    } catch (error) {
      const blockers = extractActivationBlockers(error);
      if (blockers) {
        return RepositoryResultFactory.success({ outcome: "blocked", blockers });
      }
      return RepositoryResultFactory.error(toKybException(error));
    }
  }

  async listRevocationReasons(
    caseId: string,
  ): Promise<RepositoryResult<KybMandateRevocationReason[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<
          ApiResponse<{ code: KybMandateRevocationReasonCode; label: string }[]>
        >({
          url: kybMandateEndpoints.revocationReasons(caseId),
          withAuth: true,
        }),
      (dtos) => dtos.map((d) => ({ code: d.code, label: d.label })),
    );
  }

  async getRevocationImpact(
    caseId: string,
    mandateId: string,
  ): Promise<RepositoryResult<KybMandateRevocationImpact>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybRevocationImpactDto>>({
          url: kybMandateEndpoints.revocationImpact(caseId, mandateId),
          withAuth: true,
        }),
      mapImpact,
    );
  }

  async requestRevocationMfaChallenge(
    caseId: string,
    mandateId: string,
  ): Promise<RepositoryResult<KybMandateMfaChallenge>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybMfaChallengeDto>, undefined>({
          url: kybMandateEndpoints.revocationMfaChallenge(caseId, mandateId),
          withAuth: true,
        }),
      mapChallenge,
    );
  }

  async revokeMandate(
    caseId: string,
    mandateId: string,
    input: RevokeKybMandateInput,
  ): Promise<RepositoryResult<KybMandateRevocationResult>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybRevocationDto>, RevokeKybMandateInput>({
          url: kybMandateEndpoints.revocation(caseId, mandateId),
          body: input,
          withAuth: true,
        }),
      (dto) => ({
        mandate: mapMandate(dto.mandate),
        alreadyRevoked: dto.alreadyRevoked,
        deactivatedAccesses: dto.deactivatedAccesses,
        maintainedAccesses: dto.maintainedAccesses,
        cascadedDelegations: dto.cascadedDelegations,
      }),
    );
  }

  async listDelegations(
    caseId: string,
    mandateId: string,
  ): Promise<RepositoryResult<KybMandate[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<KybMandateDto[]>>({
          url: kybMandateEndpoints.delegations(caseId, mandateId),
          withAuth: true,
        }),
      (dtos) => dtos.map(mapMandate),
    );
  }

  async createDelegation(
    caseId: string,
    mandateId: string,
    input: CreateKybDelegationInput,
  ): Promise<RepositoryResult<KybMandate>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<KybMandateDto>, CreateKybDelegationInput>({
          url: kybMandateEndpoints.delegations(caseId, mandateId),
          body: input,
          withAuth: true,
        }),
      mapMandate,
    );
  }
}
